import { useState, useEffect, useCallback } from 'react';

/**
 * useAnimations - Animation settings hook
 * Manages border, background, and particle animation states
 */
const useAnimations = (currentUser, authToken) => {
  // Border animation state
  const [borderAnimationEnabled, setBorderAnimationEnabled] = useState(false);
  const [borderAnimSettings, setBorderAnimSettings] = useState({
    intensity: 0.85,
    speed: 3,
    color1: '#06b6d4',
    color2: '#8b5cf6'
  });

  // Background animation state
  const [bgAnimationEnabled, setBgAnimationEnabled] = useState(false);
  const [bgAnimSettings, setBgAnimSettings] = useState({
    intensity: 0.12,
    speed: 8,
    color1: '#06b6d4',
    color2: '#8b5cf6'
  });

  // Particle animation state
  const [particleAnimationEnabled, setParticleAnimationEnabled] = useState(false);
  const [particleAnimSettings, setParticleAnimSettings] = useState({
    count: 50,
    speed: 1,
    size: 3,
    color1: '#06b6d4',
    color2: '#8b5cf6',
    opacity: 0.6
  });

  // Sync with global window.animationSettings
  useEffect(() => {
    const checkAnimations = () => {
      const settings = window.animationSettings;
      if (!settings) return;

      setBorderAnimationEnabled(settings.borderAnimation || false);
      setBgAnimationEnabled(settings.backgroundAnimation || false);
      setParticleAnimationEnabled(settings.enabled || false);

      setBgAnimSettings({
        intensity: settings.bgAnimIntensity || 0.12,
        speed: settings.bgAnimSpeed || 8,
        color1: settings.bgAnimColor1 || '#06b6d4',
        color2: settings.bgAnimColor2 || '#8b5cf6'
      });

      setBorderAnimSettings({
        intensity: settings.borderAnimIntensity || 0.85,
        speed: settings.borderAnimSpeed || 3,
        color1: settings.borderAnimColor1 || '#06b6d4',
        color2: settings.borderAnimColor2 || '#8b5cf6'
      });

      setParticleAnimSettings({
        count: settings.particleCount || 50,
        speed: settings.particleSpeed || 1,
        size: settings.particleSize || 3,
        color1: settings.particleColor1 || '#06b6d4',
        color2: settings.particleColor2 || '#8b5cf6',
        opacity: settings.particleOpacity || 0.6
      });
    };

    const interval = setInterval(checkAnimations, 500);
    return () => clearInterval(interval);
  }, []);

  // Sync particle animation with global window.animationSettings (for non-admin users)
  useEffect(() => {
    if (window.animationSettings && !currentUser?.is_admin) {
      window.animationSettings.enabled = particleAnimationEnabled;
      window.animationSettings.particleCount = particleAnimSettings.count;
      window.animationSettings.speed = particleAnimSettings.speed;
      window.animationSettings.size = particleAnimSettings.size / 3;
      window.animationSettings.contrast = particleAnimSettings.opacity;
    }
  }, [particleAnimationEnabled, particleAnimSettings, currentUser]);

  // Load animation preferences when paid user logs in
  useEffect(() => {
    const loadAnimationPreferences = async () => {
      if (!currentUser || !authToken || currentUser.tier?.slug === 'free') return;

      try {
        const resp = await fetch('/api/user/animation-preferences', {
          credentials: 'include'
        });
        
        if (resp.ok) {
          const data = await resp.json();
          if (data.preferences) {
            const prefs = data.preferences;
            localStorage.setItem('animationSettings', JSON.stringify(prefs));
            window.animationSettings = { ...window.animationSettings, ...prefs };

            // Update state from preferences
            if (typeof prefs.borderAnimation === 'boolean') {
              setBorderAnimationEnabled(prefs.borderAnimation);
            }
            if (typeof prefs.bgAnimation === 'boolean') {
              setBgAnimationEnabled(prefs.bgAnimation);
            }
            if (typeof prefs.particleEnabled === 'boolean') {
              setParticleAnimationEnabled(prefs.particleEnabled);
            }

            // Border settings
            if (typeof prefs.borderIntensity === 'number') {
              setBorderAnimSettings(prev => ({ ...prev, intensity: prefs.borderIntensity }));
            }
            if (typeof prefs.borderSpeed === 'number') {
              setBorderAnimSettings(prev => ({ ...prev, speed: prefs.borderSpeed }));
            }
            if (prefs.borderColor1) {
              setBorderAnimSettings(prev => ({ ...prev, color1: prefs.borderColor1 }));
            }
            if (prefs.borderColor2) {
              setBorderAnimSettings(prev => ({ ...prev, color2: prefs.borderColor2 }));
            }

            // Background settings
            if (typeof prefs.bgIntensity === 'number') {
              setBgAnimSettings(prev => ({ ...prev, intensity: prefs.bgIntensity }));
            }
            if (typeof prefs.bgSpeed === 'number') {
              setBgAnimSettings(prev => ({ ...prev, speed: prefs.bgSpeed }));
            }
            if (prefs.bgColor1) {
              setBgAnimSettings(prev => ({ ...prev, color1: prefs.bgColor1 }));
            }
            if (prefs.bgColor2) {
              setBgAnimSettings(prev => ({ ...prev, color2: prefs.bgColor2 }));
            }

            // Particle settings
            if (typeof prefs.particleCount === 'number') {
              setParticleAnimSettings(prev => ({ ...prev, count: prefs.particleCount }));
            }
            if (typeof prefs.particleSpeed === 'number') {
              setParticleAnimSettings(prev => ({ ...prev, speed: prefs.particleSpeed }));
            }
            if (typeof prefs.particleSize === 'number') {
              setParticleAnimSettings(prev => ({ ...prev, size: prefs.particleSize }));
            }
            if (prefs.particleColor1) {
              setParticleAnimSettings(prev => ({ ...prev, color1: prefs.particleColor1 }));
            }
            if (prefs.particleColor2) {
              setParticleAnimSettings(prev => ({ ...prev, color2: prefs.particleColor2 }));
            }
            if (typeof prefs.particleOpacity === 'number') {
              setParticleAnimSettings(prev => ({ ...prev, opacity: prefs.particleOpacity }));
            }
          }
        }
      } catch (e) {
        console.error('Failed to load animation preferences:', e);
      }
    };

    loadAnimationPreferences();
  }, [currentUser, authToken]);

  // Save animation preferences
  const saveAnimationPreferences = useCallback(async () => {
    if (!currentUser || currentUser.tier?.slug === 'free') return;

    const preferences = {
      borderAnimation: borderAnimationEnabled,
      borderIntensity: borderAnimSettings.intensity,
      borderSpeed: borderAnimSettings.speed,
      borderColor1: borderAnimSettings.color1,
      borderColor2: borderAnimSettings.color2,
      bgAnimation: bgAnimationEnabled,
      bgIntensity: bgAnimSettings.intensity,
      bgSpeed: bgAnimSettings.speed,
      bgColor1: bgAnimSettings.color1,
      bgColor2: bgAnimSettings.color2,
      particleEnabled: particleAnimationEnabled,
      particleCount: particleAnimSettings.count,
      particleSpeed: particleAnimSettings.speed,
      particleSize: particleAnimSettings.size,
      particleColor1: particleAnimSettings.color1,
      particleColor2: particleAnimSettings.color2,
      particleOpacity: particleAnimSettings.opacity
    };

    try {
      const csrfRes = await fetch('/api/csrf-token');
      const { token: csrfToken } = await csrfRes.json();

      await fetch('/api/user/animation-preferences', {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({ preferences })
      });

      localStorage.setItem('animationSettings', JSON.stringify(preferences));
    } catch (e) {
      console.error('Failed to save animation preferences:', e);
    }
  }, [
    currentUser,
    borderAnimationEnabled,
    borderAnimSettings,
    bgAnimationEnabled,
    bgAnimSettings,
    particleAnimationEnabled,
    particleAnimSettings
  ]);

  // Reset to defaults
  const resetToDefaults = useCallback(() => {
    const defaults = {
      borderAnimation: false,
      borderIntensity: 0.85,
      borderSpeed: 3,
      borderColor1: '#06b6d4',
      borderColor2: '#8b5cf6',
      bgAnimation: false,
      bgIntensity: 0.12,
      bgSpeed: 8,
      bgColor1: '#06b6d4',
      bgColor2: '#8b5cf6',
      particleEnabled: false,
      particleCount: 50,
      particleSpeed: 1,
      particleSize: 3,
      particleColor1: '#06b6d4',
      particleColor2: '#8b5cf6',
      particleOpacity: 0.6
    };

    setBorderAnimationEnabled(false);
    setBorderAnimSettings({
      intensity: 0.85,
      speed: 3,
      color1: '#06b6d4',
      color2: '#8b5cf6'
    });

    setBgAnimationEnabled(false);
    setBgAnimSettings({
      intensity: 0.12,
      speed: 8,
      color1: '#06b6d4',
      color2: '#8b5cf6'
    });

    setParticleAnimationEnabled(false);
    setParticleAnimSettings({
      count: 50,
      speed: 1,
      size: 3,
      color1: '#06b6d4',
      color2: '#8b5cf6',
      opacity: 0.6
    });

    if (window.animationSettings) {
      Object.assign(window.animationSettings, defaults);
    }
    localStorage.setItem('animationSettings', JSON.stringify(defaults));
  }, []);

  return {
    // Border animation
    borderAnimationEnabled,
    setBorderAnimationEnabled,
    borderAnimSettings,
    setBorderAnimSettings,

    // Background animation
    bgAnimationEnabled,
    setBgAnimationEnabled,
    bgAnimSettings,
    setBgAnimSettings,

    // Particle animation
    particleAnimationEnabled,
    setParticleAnimationEnabled,
    particleAnimSettings,
    setParticleAnimSettings,

    // Actions
    saveAnimationPreferences,
    resetToDefaults
  };
};

export default useAnimations;
